const ACCENT_PALETTE = ['#0C7D74', '#12a095', '#10b981', '#f59e0b', '#070B14', '#06b6d4'];

/**
 * رنگ ثابت برای هر سازمان (برای آواتار و UI).
 *
 * @param {string|number} id
 * @returns {string}
 */
export function organizationAccent(id) {
  const s = String(id ?? '');
  let sum = 0;
  for (let i = 0; i < s.length; i += 1) sum += s.charCodeAt(i);
  return ACCENT_PALETTE[sum % ACCENT_PALETTE.length];
}

/**
 * آدرس لوگوی سازمان — فقط وقتی واقعاً عکس دارد.
 * حرف اول فقط وقتی این مقدار خالی است باید نمایش داده شود.
 *
 * @param {Record<string, unknown>|null|undefined} org
 * @returns {string|null}
 */
export function organizationLogoUrl(org) {
  if (!org || typeof org !== 'object') return null;
  const nested = org.data && typeof org.data === 'object' && !Array.isArray(org.data)
    ? org.data.logo_url
    : null;
  const candidates = [org.logo_url, org.logo, org.avatar, org.image_url, nested];
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

/**
 * ادغام لیست سازمان‌های auth با پاسخ ناقص‌تر (مثلاً GET /profile)
 * تا logo_url و بقیهٔ فیلدهای قبلی با آرایهٔ بدون لوگو جایگزین نشوند.
 *
 * @param {unknown[]|null|undefined} existing
 * @param {unknown[]|null|undefined} incoming
 * @returns {unknown[]}
 */
export function mergeAuthOrganizations(existing, incoming) {
  if (!Array.isArray(incoming)) return Array.isArray(existing) ? existing : [];
  const prevById = new Map(
    (Array.isArray(existing) ? existing : [])
      .filter((org) => org && typeof org === 'object' && org.id != null)
      .map((org) => [String(org.id), org]),
  );
  return incoming.map((org) => {
    if (!org || typeof org !== 'object' || org.id == null) return org;
    const prev = prevById.get(String(org.id));
    if (!prev || typeof prev !== 'object') return org;
    const incomingLogo = organizationLogoUrl(org);
    const keepPreviousLogo = !Object.prototype.hasOwnProperty.call(org, 'logo_url')
      && !Object.prototype.hasOwnProperty.call(org, 'logo');
    return {
      ...prev,
      ...org,
      logo_url: incomingLogo || (keepPreviousLogo ? organizationLogoUrl(prev) : null),
    };
  });
}

/**
 * نام نمایشی سازمان از آبجکت API.
 *
 * @param {Record<string, unknown>} org
 * @returns {string}
 */
export function organizationDisplayName(org) {
  if (!org || typeof org !== 'object') return 'سازمان';
  const nested = org.data && typeof org.data === 'object' ? org.data.name : null;
  const name = org.name ?? org.title ?? org.slug ?? nested;
  if (typeof name === 'string' && name.trim() && name.trim() !== '—') return name.trim();
  if (org.id != null) return `سازمان ${org.id}`;
  return 'سازمان';
}

/**
 * گزینه Select برای سازمان — جلوگیری از نمایش UUID وقتی options هنوز لود نشده.
 */
export function organizationToOption(org) {
  const value = org?.id;
  if (value == null) return null;
  return {
    value,
    label: organizationDisplayName(org),
  };
}

/**
 * نقش کاربر در سازمان (در صورت وجود در پاسخ API).
 *
 * @param {Record<string, unknown>} org
 * @returns {string}
 */
export function organizationRoleLabel(org) {
  if (!org || typeof org !== 'object') return 'عضو';
  const candidates = [
    org.role,
    org.my_role,
    org.membership_role,
    org.role_name,
    org.pivot && typeof org.pivot === 'object' ? org.pivot.role : null,
    org.pivot && typeof org.pivot === 'object' ? org.pivot.role_name : null,
  ];
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return 'عضو';
}

const ORG_ROLE_LABELS_FA = {
  owner: 'مالک سازمان',
  organization_manager: 'مدیر سازمان',
  meeting_manager: 'مدیر جلسه',
  manager: 'مدیر واحد / کارگروه',
  room_manager: 'مدیر اتاق‌ها',
  member: 'عضو',
  observer: 'مشاهده‌گر',
  guest: 'مهمان خارجی',
  admin: 'مدیر',
  organization_finance_manager: 'مدیر مالی',
};

/**
 * برچسب فارسی نقش سازمانی از slug انگلیسی.
 *
 * @param {string|null|undefined} roleName
 * @returns {string}
 */
export function organizationRoleLabelFa(roleName, displayName) {
  if (typeof displayName === 'string' && displayName.trim()) {
    const systemLabel = ORG_ROLE_LABELS_FA[String(roleName || '').trim().toLowerCase()];
    return /[a-z]/i.test(displayName) && systemLabel ? systemLabel : displayName.trim();
  }
  if (typeof roleName !== 'string' || !roleName.trim()) return 'عضو';
  const key = roleName.trim().toLowerCase();
  return ORG_ROLE_LABELS_FA[key] ?? roleName.trim();
}

/**
 * آیا سازمان، فضای کاری شخصی کاربر است؟ (`is_personal` از `/auth/organizations` یا `ensure-personal-workspace`)
 *
 * @param {Record<string, unknown>} org
 * @returns {boolean}
 */
export function organizationIsPersonal(org) {
  if (!org || typeof org !== 'object') return false;
  return Boolean(org.is_personal);
}

/**
 * آیا کاربر حداقل یک فضای کاری شخصی دارد؟
 *
 * @param {unknown[]|null|undefined} organizations
 * @returns {boolean}
 */
export function userHasPersonalOrganization(organizations) {
  if (!Array.isArray(organizations)) return false;
  return organizations.some((org) => organizationIsPersonal(org));
}

/**
 * آیا کاربر می‌تواند فضای شخصی بسازد؟ (از API یا محاسبه محلی)
 *
 * @param {{ can_create_personal_organization?: boolean } | null | undefined} profileOrFlags
 * @returns {boolean}
 */
export function profileCanCreatePersonalOrganization(profileOrFlags) {
  if (!profileOrFlags || typeof profileOrFlags !== 'object') return false;
  return profileOrFlags.can_create_personal_organization === true;
}

/**
 * برچسب نوع Workspace فعال — سازمان در برابر فضای شخصی.
 *
 * @param {Record<string, unknown>|null|undefined} org
 * @returns {string}
 */
export function workspaceContextLabel(org) {
  if (organizationIsPersonal(org)) return 'فضای کاری شخصی';
  return 'سازمان فعال';
}

/**
 * آیا کاربر بیش از یک Workspace برای سوئیچ دارد؟
 *
 * @param {unknown[]|null|undefined} organizations
 * @returns {boolean}
 */
export function canSwitchWorkspace(organizations) {
  return Array.isArray(organizations) && organizations.length > 1;
}

/**
 * زیرعنوان چیپ سوئیچر: نوع Workspace و در صورت چندعضویت «تغییر سازمان».
 *
 * @param {Record<string, unknown>|null|undefined} org
 * @param {unknown[]|null|undefined} organizations
 * @param {{ canSwitch?: boolean }} [options]
 * @returns {string}
 */
export function workspaceSwitcherCaption(org, organizations, options = {}) {
  const label = workspaceContextLabel(org);
  const switchable = options.canSwitch ?? canSwitchWorkspace(organizations);
  if (switchable) return `${label} · تغییر سازمان`;
  return label;
}

/**
 * تولتیپ حالت جمع‌شدهٔ سوئیچر.
 *
 * @param {Record<string, unknown>|null|undefined} org
 * @param {unknown[]|null|undefined} organizations
 * @param {{ canSwitch?: boolean }} [options]
 * @returns {string}
 */
export function workspaceSwitcherTooltip(org, organizations, options = {}) {
  const caption = workspaceSwitcherCaption(org, organizations, options);
  const name = org ? organizationDisplayName(org) : 'بدون سازمان';
  const role = workspaceSwitcherRoleCaption(org);
  if (role) return `${caption}: ${name} — ${role}`;
  return `${caption}: ${name}`;
}

/**
 * نقش کاربر در سازمان فعال — برای نمایش زیر نام سازمان در سایدبار.
 *
 * @param {Record<string, unknown>|null|undefined} org
 * @returns {string}
 */
export function workspaceSwitcherRoleCaption(org) {
  if (!org) return '';
  if (organizationIsPersonal(org)) return 'فضای کاری شخصی';
  return organizationRoleLabelFa(organizationRoleLabel(org), org.role_display_name);
}
